import { Component, ViewChild, AfterViewInit, OnDestroy, OnInit, Input} from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription, combineLatest, forkJoin } from 'rxjs';
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';

import { filter, tap, mergeMap } from 'rxjs/operators';

import { AuthService } from '../../../../services/auth.service';
import { IssueSearchSpec, IssuesService } from '../../../../common/services/issues.service';
import { OrganizationService } from '../../../../common/services/organization.service';
import { WellService } from '../../../../common/services/well.service';
import { RouterLinkRendererComponent } from "../../../../common/components/ag-grid/router-link-renderer.component";

import { GISPoint, GISPointTypes, Well, WellPermittingModifiers } from '../../../../common/models/well.model';
import { Issue, IssuesConfig } from '../../../../common/models/issue.model';
import { Organization } from '../../../../common/models/organization.model';
import { WellDirection } from '../../../../form-controls/components/new-casing-plan/models/casing-plan.model';
import { AgGridAngular } from 'ag-grid-angular';
import { IBooleanFilterParams, AgGridBooleanFilterComponent } from '../../../../common/components/ag-grid/filters/boolean-filter.component';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { DetailsComponent } from '../../../../common/components/data-table/columns/details/details.component';
import { FlaggedComponent } from '../../../../common/components/data-table/columns/flagged/flagged.component';
import { DomainValue } from '../../../../models/domain.model';
import { DomainDefinitionService } from '../../../../builder/services/domain-definition.service';
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { CheckboxRendererComponent } from '../../../../common/components/ag-grid/checkbox-renderer.component';

@Component({
    selector: 'well-widget',
    templateUrl: './well-widget.component.html',
    styleUrls: ['./well-widget.component.scss'],
    standalone: false
})
export class WellWidgetComponent implements AfterViewInit, OnDestroy, OnInit {
    @Input() widgetTitle: string;

    @ViewChild(AgGridAngular, { static: true })
    public _table: AgGridAngular;
    organization: Organization;
    data: Well[];
    _wellIds: string[];
    _wellIssues: Issue[];
    dataSubscriptions: Subscription = new Subscription();
    transitioning: boolean = false;
	public gridOptions: GridOptions;
	private gridApi?: GridApi;
    _columns: ColDef[];
    _wellHasIssueColumn: ColDef;
    _buyerFAColumn: ColDef;
    _booleanfilterParams = { isInServerSideFilterMode: false, } as IBooleanFilterParams;
    _allWellTypeDomainValues: DomainValue[];
    _allWellTypeDescriptionDomainValues: DomainValue[];
    _allWellDispositionDomainValues: DomainValue[];
    _allWellDispositionLegacyTrackingValues: DomainValue[];
    _formations: DomainValue[];

    config: IssuesConfig;

    buildColumns() {
        this._columns = [
            {
                headerClass: "wrapped",
                cellRenderer: RouterLinkRendererComponent,
                cellRendererParams: {
                    openInNewWindow: true,
                    textField: "id",
                    url: (data: Well) => {
                        return `${this._router.parseUrl(this._router.url).root}dashboard/view/well/${data.id}`;
                    }
                },
                headerName: 'API #',
                headerTooltip: 'API #',
                field: 'id',
                tooltipField: 'id',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 130,
            },
            {
                headerClass: "wrapped",
                headerName: 'Well Name',
                headerTooltip: 'Well Name',
                field: 'lease.name',
                tooltipField: 'lease.name',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 150,
            },
            {
                headerClass: "wrapped",
                headerName: 'Well Number',
                headerTooltip: 'Well Number',
                field: 'wellNumber',
                tooltipField: 'wellNumber',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 140,
            },
            {
                headerClass: "wrapped",
                headerName: 'Well Disposition',
                headerTooltip: 'Well Disposition',
                valueGetter: this.formatWellDomainFieldsType('wellStatus.disposition'),
                tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.disposition'),
                minWidth: 130,
            },
            {
                headerClass: "wrapped",
                headerName: 'Legacy Tracking',
                headerTooltip: 'Legacy Tracking',
                valueGetter: this.formatWellDomainFieldsType('wellStatus.legacyTracking'),
                tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.legacyTracking'),
                minWidth: 130,
            },
            {
                headerClass: "wrapped",
                headerName: 'Owner Name',
                headerTooltip: 'Owner Name',
                field: 'organizationName',
                tooltipField: 'organizationName',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 150,
            },
            {
                headerClass: "wrapped",
                headerName: 'Owner Number',
                headerTooltip: 'Owner Number',
                field: 'organizationId',
                tooltipField: 'organizationId',
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 145,
            },
            {
                headerClass: "wrapped",
                headerName: 'County Name',
                headerTooltip: 'County Name',
                field: 'location.gisPoints',
                tooltipValueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getCountyName),
                valueFormatter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getCountyName),
                valueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getCountyName),
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 135,
            },
            {
                headerClass: "wrapped",
                headerName: 'Township  Name',
                headerTooltip: 'Township  Name',
                field: 'location.gisPoints',
                tooltipValueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getTownshipName),
                valueFormatter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getTownshipName),
                valueGetter: this._gridFormatterService.arraySelectorFormatter('location.gisPoints', this.getTownshipName),
                filter: "agTextColumnFilter",
                filterParams: this._gridFormatterService.defaultTextFilterParams,
                minWidth: 150,
            },
            {
                headerClass: "wrapped",
                headerName: 'Tract < 5 Acres',
                headerTooltip: 'Tract < 5 Acres',
                valueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'WithoutFiveAcres'),
                tooltipValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'WithoutFiveAcres'),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                minWidth: 150,
            },
            {
                headerClass: "wrapped",
                headerName: 'Release of Leases',
                headerTooltip: 'Release of Leases',
                valueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ReleaseOfLease'),
                tooltipValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ReleaseOfLease'),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                minWidth: 160,
            },
            {
                headerClass: "wrapped",
                headerName: 'Permitted Urban',
                headerTooltip: 'Permitted Urban',
                valueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ConsideredUrbanForPermitting'),
                tooltipValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ConsideredUrbanForPermitting'),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                filterValueGetter: this._gridFormatterService.flagEnumFormatter('wellStatus.permittingModifiers', WellPermittingModifiers, 'ConsideredUrbanForPermitting', true),
                minWidth: 150,
            },
            //{
            //    headerName: 'Urban',
            //    headerTooltip: 'Urban',
            //    field: 'location.isUrban',
            //    valueFormatter: this._gridFormatterService.formatBooleanToYesNo('location.isUrban'),
            //    tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo('location.isUrban'),
            //    filter: "booleanFilter",
            //    filterParams: this._booleanfilterParams,
            //    minWidth: 100,
            //},
            //{
            //    name: 'Lease Name',
            //    index: 2,
            //    sortable: true,
            //    filterable: true,
            //    resizable: true,
            //    objectProperty: {
            //        name: 'Name',
            //        path: '$.lease.name'
            //    } as ObjectProperties,
            //},
            {
                headerClass: "wrapped",
                headerName: 'Horizontal',
                headerTooltip: 'Horizontal',
                field: 'location.slant',
                valueFormatter: this.isHorizontal(),
                tooltipValueGetter: this.isHorizontal(),
                filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                filterValueGetter: this.isHorizontal(true),
                minWidth: 125,
            },
            {
                headerClass: "wrapped",
                headerName: 'Slant',
                headerTooltip: 'Slant',
                field: 'location.slant',
                valueFormatter: this.formatWellSlant(),
                tooltipValueGetter: this.formatWellSlant(),
                minWidth: 100,
            },
            {
                headerClass: "wrapped",
                headerName: 'Well Type',
                headerTooltip: 'Well Type',
                valueGetter: this.formatWellDomainFieldsType('wellStatus.type.type'),
                tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.type.type'),
                minWidth: 115,
            },
            {
                headerClass: "wrapped",
                headerName: 'Well Type Description',
                headerTooltip: 'Well Type Description',
                valueGetter: this.formatWellDomainFieldsType('wellStatus.type.description'),
                tooltipValueGetter: this.formatWellDomainFieldsType('wellStatus.type.description'),
                minWidth: 165,
            },
            {
                headerClass: "wrapped",
                headerName: 'Target Formation',
                headerTooltip: 'Target Formation',
                field: 'proposedFormation',
                valueGetter: this.formatWellDomainFieldsType('proposedFormation'),
                tooltipValueGetter: this.formatWellDomainFieldsType('proposedFormation'),
                minWidth: 155,
            },
            {
                headerClass: "wrapped",
                headerName: 'Deepest Formation',
                headerTooltip: 'Deepest Formation',
                field: 'deepestFormationPenetrated',
                valueGetter: this.formatWellDomainFieldsType('deepestFormationPenetrated'),
                tooltipValueGetter: this.formatWellDomainFieldsType('deepestFormationPenetrated'),
                minWidth: 165,
            },
            {
                headerClass: "wrapped",
                headerName: 'Date Spud',
                headerTooltip: 'Date Spud',
                field: 'dateOfOriginalSpud',
                tooltipValueGetter: this._gridFormatterService.formatDate("dateOfOriginalSpud"),
                valueFormatter: this._gridFormatterService.formatDate("dateOfOriginalSpud"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this._gridFormatterService.defaultDateFilterParams,
                minWidth: 155,
            },
            {
                headerClass: "wrapped",
                headerName: 'Date T.D.',
                headerTooltip: 'Date T.D.',
                field: 'dateTotalDepthReached',
                tooltipValueGetter: this._gridFormatterService.formatDate("dateTotalDepthReached"),
                valueGetter: this._gridFormatterService.formatDate("dateTotalDepthReached"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this._gridFormatterService.defaultDateFilterParams,
                minWidth: 155,
            },
            {
                headerClass: "wrapped",
                headerName: 'Date P&A',
                headerTooltip: 'Date P&A',
                field: 'plugging.datePluggedAndAbandoned',
                tooltipValueGetter: this._gridFormatterService.formatDate("plugging.datePluggedAndAbandoned"),
                valueGetter: this._gridFormatterService.formatDate("plugging.datePluggedAndAbandoned"),
                comparator: dateComparator,
                filter: "agDateColumnFilter",
                filterParams: this._gridFormatterService.defaultDateFilterParams,
                minWidth: 155,
            },
            {
                headerClass: "wrapped",
                headerName: 'Driller T.D.',
                headerTooltip: 'Driller T.D.',
                field: 'depths.drillersMeasuredVerticalDepth',
                tooltipField: 'depths.drillersMeasuredVerticalDepth',
                filter: "agNumberColumnFilter",
                filterParams: this._gridFormatterService.defaultNumberFilterParams,
                minWidth: 120,
            },
            {
                headerClass: "wrapped",
                headerName: 'Logger T.D.',
                headerTooltip: 'Logger T.D.',
                field: 'depths.loggerMeasuredVerticalDepth',
                tooltipField: 'depths.loggerMeasuredVerticalDepth',
                filter: "agNumberColumnFilter",
                filterParams: this._gridFormatterService.defaultNumberFilterParams,
                minWidth: 120,
            },
            {
                headerClass: "wrapped",
                headerName: "Requires Bonding",
                headerTooltip: "Requires Bonding",
                field: "requiresBonding",
                tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresBonding"),
                filterValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresBonding"),
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "requiresBonding",
                    description: "Requires Bonding",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; }
                },
                editable: false,
                suppressMovable: true,
                minWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Requires Insurance",
                headerTooltip: "Requires Insurance",
                field: "requiresInsurance",
                tooltipValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresInsurance"),
                filterValueGetter: this._gridFormatterService.formatBooleanToYesNo("requiresInsurance"),
                cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "requiresInsurance",
                    description: "Requires Insurance",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; }
                },
                editable: false,
                suppressMovable: true,
                minWidth: 125
            } as ColDef
        ] as ColDef[];

        this._buyerFAColumn = {
            headerClass: "wrapped",
            headerName: 'Buyer FA',
            field: 'faOrganizationId',
            printable: false,
            cellRenderer: FlaggedComponent,
            cellRendererParams: { values: 'icon-blocked' },
            valueFormatter: this.buyerLacksFA(),
            tooltipValueGetter: this.buyerLacksFA(true),
            filter: AgGridBooleanFilterComponent,
            filterParams: this._booleanfilterParams,
            filterValueGetter: this.buyerLacksFA(),
            minWidth: 120,
        } as ColDef;

        this._wellHasIssueColumn = {
            headerClass: "wrapped",
            headerName: 'Open Issue',
            field: 'id',
            cellRenderer: FlaggedComponent,
            cellRendererParams: { values: 'icon-alert' },
            valueGetter: this.hasIssues(),
            tooltipValueGetter: this.hasIssues(true),
            filter: AgGridBooleanFilterComponent,
            filterParams: this._booleanfilterParams,
            filterValueGetter: this.hasIssues(),
            minWidth: 120,
        } as ColDef;
    }

    

    openIssueToolTipFormatter() {
        return (obj: any) => {
            let hasOpenIssue = false;

            if (obj && obj.data && obj.data.id) {
                if (this._wellIssues.some(a => a.wellId == obj.data.id))
                    hasOpenIssue = true;
            }

            return hasOpenIssue ? "Well has open Issues" : "Well has no open issues";
        }
    }

    constructor(
        public _wellService: WellService,
        public _authService: AuthService,
        public _router: Router,
        public _organizationService: OrganizationService,
        public _issuesService: IssuesService,
        public _gridFormatterService: GridFormatterService,
        public _domainService: DomainDefinitionService)
    {
        this.config = (window as any)["issuesConfig"];
        this.buildColumns();
    }

    addColumnToEnd(columnToPush: ColDef) {
        this._columns.push(columnToPush);
        this._table.api.setGridOption('columnDefs', this._columns);
        this._table.api.sizeColumnsToFit();
    }

    ngOnInit() {
        this.gridOptions = this._gridFormatterService.BuildDefaultGridOptionsWithColumns(this._columns);
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.gridOptions.headerHeight = 50;
        let typeObs$ = this._domainService.getValues("WELL_TYP").pipe(tap((domains: DomainValue[]) => {
            if (domains) this._allWellTypeDomainValues = domains;
        }));

        let descObs$ = this._domainService.getValues("WLTYP_DESC").pipe(tap((domains: DomainValue[]) => {
            if (domains) this._allWellTypeDescriptionDomainValues = domains;
        }));

        let statusObs$ = this._domainService.getValues("STATUS_DESC").pipe(tap((domains: DomainValue[]) => {
            if (domains) this._allWellDispositionDomainValues = domains;
        }));

        let reasonObs$ = this._domainService.getValues("STATUS_REAS").pipe(tap((domains: DomainValue[]) => {
            if (domains) this._allWellDispositionLegacyTrackingValues = domains;
        }));

        let formationsObs$ = this._domainService.getValues("FORMATION").pipe(tap((domains: DomainValue[]) => {
            if (domains) this._formations = domains;
        }));

        let dataSubs$ = forkJoin([typeObs$, descObs$, statusObs$, reasonObs$, formationsObs$]).pipe(
            mergeMap((data) => {
                return this._organizationService.getOrganization(this._authService.User.organizationId);
            }),
            mergeMap((org) => {
                this.organization = org;
                return this._wellService.getWellsForOrganizationWithFinancialAssuranceFlags(org.id, org.historicalOwnerNumber); //change
            })            
        ).subscribe({
            next: (wells) => {
                this.data = wells;
                this.fetchWellIssues();
            }, 
            error: (error) => { console.error(error); }
        });

        this.dataSubscriptions.add(dataSubs$);

        this._router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.dataSubscriptions.unsubscribe();
            if (routerEvent.url !== '/dashboard/view/external_default' && routerEvent.url !== '/dashboard/view/ricsDashboard') {
                this.transitioning = true;
            }
        });
    }

    ngOnDestroy() {
        if (this.dataSubscriptions) {
            this.dataSubscriptions.unsubscribe();
        }
    }

    ngAfterViewInit() {
    }

    getCountyName(array: GISPoint[]) {
        let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
        return pt && pt.county ? pt.county.name : '';
    }

    getTownshipName(array: GISPoint[]) {
        let pt = array.find(x => x.pointType === GISPointTypes.WellHead);
        return pt && pt.township ? pt.township.name : '';
    }

    isHorizontal(returnAsBool: boolean = false): any {
        return (obj: any) => {
            if (obj.data) {
                var path = "$.location.slant";
                let fieldValue = this._gridFormatterService.tryGetValue(obj.data, path);

                if (!returnAsBool)
                    return fieldValue == "Horizontal" ? "Yes" : "No";
                else
                    return fieldValue == "Horizontal";
            }
        }
    }

    hasIssues(returnTooltip: boolean = false): any {
        return (obj: any) => {
            let hasOpenIssue = false;
            let matchingWellIssues: Issue[] = [];
            if (obj && obj.data && obj.data.id) {
                matchingWellIssues = this._wellIssues.filter(a => a.wellId == obj.data.id);
                if (matchingWellIssues.length > 0)
                    hasOpenIssue = true;
            }

            if (returnTooltip)
                return hasOpenIssue ? "Well has open Issues : " + matchingWellIssues[0].description : "Well has no open issues";
            else
                return hasOpenIssue;
        }
    }

    buyerLacksFA(returnTooltip: boolean = false): any {
        return (obj: any) => {
            let buyerLacksFa = false;

            if (obj.data && obj.data.faOrganizationId != null && obj.data.faOrganizationId != undefined && obj.data.faOrganizationId != "")
                buyerLacksFa = true;

            if (returnTooltip)
                return buyerLacksFa ? "Buyer Lacks Necessary FA" : "Buyer Has Necessary FA";
            else
                return buyerLacksFa;
        }
    }

    formatWellSlant() {
        return (obj: any) => {
            if (obj.data) {
                let slantCode = '';
                var path = "$.location.slant";
                let fieldValue = this._gridFormatterService.tryGetValue(obj.data, path);

                switch (fieldValue) {
                    case WellDirection.Directional:
                        slantCode = "D";
                        break;
                    case WellDirection.Vertical:
                        slantCode = "V";
                        break;
                    default:
                        slantCode = "H";
                        break;
                };

                return slantCode;
            }
        }
    }


    formatWellDomainFieldsType(field: string) {
        return (obj: any) => {
            if (obj.data) {
                var path = "$." + field;
                let fieldValue = this._gridFormatterService.tryGetValue(obj.data, path);
                let domainValues: DomainValue[] = [];
                switch (field) {
                    case 'wellStatus.disposition':
                        domainValues = this._allWellDispositionDomainValues;
                        break;
                    case 'wellStatus.legacyTracking':
                        domainValues = this._allWellDispositionLegacyTrackingValues;
                        break;
                    case 'wellStatus.type.type':
                        domainValues = this._allWellTypeDomainValues;
                        break;
                    case 'wellStatus.type.description':
                        domainValues = this._allWellTypeDescriptionDomainValues;
                        break;
                    case 'proposedFormation':
                    case 'deepestFormationPenetrated':
                        domainValues = this._formations;
                        break;
                }

                if (domainValues && domainValues.length > 0) {

                    let matchingDomainValue = domainValues.filter(a => a.Code == fieldValue);
                    if (matchingDomainValue.length > 0) {
                        let domainValueToReturn = matchingDomainValue[0].Definition;
                        if (field.indexOf("Formation") !== -1) {
                            domainValueToReturn = this.capitalizeWord(matchingDomainValue[0].Definition);
                        }
                        return domainValueToReturn;
                    }
                    else
                        if (field.indexOf("Formation") !== -1) {
                            fieldValue = this.capitalizeWords(fieldValue, " ");
                            fieldValue = this.capitalizeWords(fieldValue, "/");
                        }
                        return fieldValue;
                }
                else {
                    if (field.indexOf("Formation") !== -1) {
                        fieldValue = this.capitalizeWords(fieldValue, " ");
                        fieldValue = this.capitalizeWords(fieldValue, "/");
                    }
                    return fieldValue;
                }
            }
            else return "";
        }
    }

    private capitalizeWords(word: string, delimiter: string): string {

        let capitalizedWordParts: string[] = [];
        if (word != undefined && word.indexOf(delimiter) !== -1) {
            let wordParts = word.split(delimiter);
            for (let i = 0; i < wordParts.length; i++) {
                let capitalizedWord = this.capitalizeWord(wordParts[i]);
                capitalizedWordParts.push(capitalizedWord);
            }
        }
        else {
            if (word != undefined) {
                let capitalizedWord = this.capitalizeWord(word);
                capitalizedWordParts.push(capitalizedWord);
            }
        }
        return capitalizedWordParts.join(" ");
    }

    private capitalizeWord(word: string): string {
        let capitalizedWord = "";
        if (word != undefined) {
            capitalizedWord = word.toUpperCase();
        }

        return capitalizedWord;
    }

    private fetchWellIssues(): void {
        if (!this.transitioning) {
            this._wellIds = this.data.map(a => a.id);

            let issueSearchSpec = {
                issueIsResolved: false,
                wellIds: this._wellIds
            } as IssueSearchSpec;

            var wellsLackingBuyerFA = this.data.filter(a => a.faOrganizationId && (a.faOrganizationId == this.organization.id || a.faOrganizationId == this.organization.historicalOwnerNumber));
            if (wellsLackingBuyerFA.length > 0) {
                this.addColumnToEnd(this._buyerFAColumn);
            }

            const showIssues: boolean = this.config.showIssuesContent.toString().toLocaleLowerCase() === 'true';
            if (showIssues) {
                this.dataSubscriptions.add(this._issuesService.getIssues(issueSearchSpec).subscribe((issues) => {
                    this._wellIssues = issues;
                    if (this._wellIds) {
                        var wellsHaveOpenIssues = this._wellIds.filter(a => issues.some(b => b.wellId == a));
                        if (wellsHaveOpenIssues.length > 0) {
                            this.addColumnToEnd(this._wellHasIssueColumn);
                        }
                    }
                }, (error) => {
                    console.log(error);
                }));
            }           
        }
    }
}
